/**
 * cart-metal-bundle.js
 * Handles:
 * 1. Select Metal swatch selection & dynamic header/input updates.
 * 2. Multi-item native add-to-cart submission to /cart/add.js with parent_id & lineRelationship.
 * 3. Synchronized removal and quantity updates for nested child metal items in cart.
 * 4. Bulletproof double-submission prevention and event propagation stopping.
 */

document.addEventListener('DOMContentLoaded', function() {
  initMetalSwatches();
  initFormSubmitHandler();
  initCartSyncHandlers();
});

document.addEventListener('shopify:section:load', function() {
  initMetalSwatches();
  initFormSubmitHandler();
});

function syncMetalInputs(metalId, metalTitle, targetForm) {
  const scope = targetForm ? (targetForm.closest('.product, [data-section-type="product"], .product-single') || targetForm) : document;
  scope.querySelectorAll('[data-metal-input-id], [data-form-metal-input-id], input[name="properties[_selected_metal_id]"]').forEach(function(input) {
    input.value = metalId;
  });
  scope.querySelectorAll('[data-metal-input-title], [data-form-metal-input-title], input[name="properties[Metal]"]').forEach(function(input) {
    input.value = metalTitle;
  });
}

function getSelectedMetal(form) {
  const productContainer = form.closest('.product, [data-section-type="product"], .product-single') || document;
  
  let metalInputId = form.querySelector('[name="properties[_selected_metal_id]"]') ||
                     productContainer.querySelector('[data-metal-input-id]') ||
                     productContainer.querySelector('.select-metal-swatch.is-selected');
  let metalVariantId = '';
  let metalTitle = '';

  if (metalInputId) {
    metalVariantId = metalInputId.value || metalInputId.getAttribute('data-metal-id') || '';
  }

  let metalTitleInput = form.querySelector('[name="properties[Metal]"]') ||
                        productContainer.querySelector('[data-metal-input-title]') ||
                        productContainer.querySelector('.select-metal-swatch.is-selected');
  if (metalTitleInput) {
    metalTitle = metalTitleInput.value || metalTitleInput.getAttribute('data-metal-title') || '';
  }

  return { id: metalVariantId, title: metalTitle };
}

function initMetalSwatches() {
  document.querySelectorAll('[data-select-metal-block]').forEach(function(block) {
    const titleEl = block.querySelector('[data-selected-metal-title]');
    const priceEl = block.querySelector('[data-selected-metal-price]');
    const swatches = block.querySelectorAll('.select-metal-swatch');
    const parentForm = block.closest('form') || block.closest('.product, [data-section-type="product"]')?.querySelector('form[action*="/cart/add"]');

    // Sync initial active swatch values into form inputs
    const initialActive = block.querySelector('.select-metal-swatch.is-selected') || swatches[0];
    if (initialActive) {
      const initId = initialActive.getAttribute('data-metal-id');
      const initTitle = initialActive.getAttribute('data-metal-title');
      if (initId) syncMetalInputs(initId, initTitle, parentForm);
    }

    swatches.forEach(function(swatch) {
      swatch.addEventListener('click', function(e) {
        e.preventDefault();
        const metalId = swatch.getAttribute('data-metal-id');
        const metalTitle = swatch.getAttribute('data-metal-title');
        const metalPrice = swatch.getAttribute('data-metal-price');

        // Highlight active swatch
        swatches.forEach(function(s) {
          s.classList.remove('is-selected');
          s.style.border = '1px solid #dcdcdc';
        });
        swatch.classList.add('is-selected');
        swatch.style.border = '2px solid #000000';

        // Update dynamic header label
        if (titleEl) titleEl.textContent = metalTitle;
        if (priceEl) priceEl.textContent = metalPrice;

        // Sync inputs across form elements
        syncMetalInputs(metalId, metalTitle, parentForm);
      });
    });
  });
}

function initFormSubmitHandler() {
  document.querySelectorAll('form[action*="/cart/add"]').forEach(function(form) {
    if (form.getAttribute('data-metal-bundle-initialized')) return;
    form.setAttribute('data-metal-bundle-initialized', 'true');

    // Intercept click on submit buttons in capture phase to prevent double trigger
    const submitBtns = form.querySelectorAll('[type="submit"], [name="add"], .product-form__submit');
    submitBtns.forEach(function(btn) {
      btn.addEventListener('click', function(btnEvt) {
        if (form.dataset.isSubmitting === 'true') {
          btnEvt.preventDefault();
          btnEvt.stopPropagation();
          btnEvt.stopImmediatePropagation();
          return false;
        }
      }, true);
    });

    form.addEventListener('submit', function(e) {
      if (form.dataset.isSubmitting === 'true') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }

      const metalInfo = getSelectedMetal(form);
      const metalVariantId = metalInfo.id;
      const metalTitle = metalInfo.title;

      if (!metalVariantId) return; // Standard submission if no metal selected

      // Lock form submission immediately to prevent duplicate requests
      form.dataset.isSubmitting = 'true';

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const mainVariantInput = form.querySelector('[name="id"]');
      const quantityInput = form.querySelector('[name="quantity"]');
      const mainVariantId = mainVariantInput ? mainVariantInput.value : '';
      const mainQty = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      // Get main product title
      let mainProductTitle = '';
      const titleEl = document.querySelector('h1.product__title') || 
                      document.querySelector('h1.cart__form-item-title') ||
                      document.querySelector('h1') || 
                      form.closest('.product')?.querySelector('h1');
      if (titleEl) {
        mainProductTitle = titleEl.textContent.trim();
      }

      const bundleTimestamp = Date.now();
      const bundleId = 'bundle_' + bundleTimestamp;

      // Extract all form properties
      const mainProperties = {
        '_bundle_id': bundleId,
        '_selected_metal_id': String(metalVariantId),
        'Metal': metalTitle
      };

      const formData = new FormData(form);
      for (let [key, val] of formData.entries()) {
        if (key.startsWith('properties[')) {
          const propName = key.replace(/^properties\[/, '').replace(/\]$/, '');
          if (propName !== '_selected_metal_id') {
            mainProperties[propName] = val;
          }
        }
      }

      const payloadWithParent = {
        items: [
          {
            id: Number(mainVariantId),
            quantity: Number(mainQty),
            properties: mainProperties
          },
          {
            id: Number(metalVariantId),
            quantity: Number(mainQty),
            parent_id: Number(mainVariantId), // Native lineRelationship for Checkout
            properties: {
              '_parent_id': bundleId,
              'Option For': mainProductTitle
            }
          }
        ]
      };

      const payloadStandard = {
        items: [
          {
            id: Number(mainVariantId),
            quantity: Number(mainQty),
            properties: mainProperties
          },
          {
            id: Number(metalVariantId),
            quantity: Number(mainQty),
            properties: {
              '_parent_id': bundleId,
              'Option For': mainProductTitle
            }
          }
        ]
      };

      const submitBtn = form.querySelector('[type="submit"]') || form.querySelector('[name="add"]');
      if (submitBtn) {
        submitBtn.setAttribute('disabled', 'disabled');
        submitBtn.classList.add('loading');
      }

      const rootUrl = window.Shopify?.routes?.root || '/';
      const addUrl = rootUrl.endsWith('/') ? rootUrl + 'cart/add.js' : rootUrl + '/cart/add.js';

      const postData = (payloadToPost) => {
        return fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payloadToPost)
        }).then(res => res.json());
      };

      postData(payloadWithParent)
        .then(data => {
          if (data.status == 422 || data.status >= 400 || (data.description && data.description.includes('parent_id'))) {
            return postData(payloadStandard);
          }
          return data;
        })
        .then(data => {
          if (submitBtn) {
            submitBtn.removeAttribute('disabled');
            submitBtn.classList.remove('loading');
          }

          // Emit cart update events for themeJS & quick-cart
          if (window.flu?.events) {
            window.flu.events.emit('cart:updated');
            window.flu.events.emit('quick-cart:updated');
            window.flu.events.emit('quick-cart:open');
          }
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('quick-cart:updated'));
          document.dispatchEvent(new CustomEvent('quick-cart:open'));

          // If quick cart exists in DOM, trigger its refresh
          const quickCartEl = document.querySelector('[data-section-type="quick-cart"]');
          if (!quickCartEl) {
            window.location.href = rootUrl.endsWith('/') ? rootUrl + 'cart' : rootUrl + '/cart';
          }
        })
        .catch(err => {
          console.error('Error adding metal bundle to cart:', err);
          if (submitBtn) {
            submitBtn.removeAttribute('disabled');
            submitBtn.classList.remove('loading');
          }
        })
        .finally(() => {
          // Unlock submission after 1 second debounce window
          setTimeout(() => {
            form.dataset.isSubmitting = 'false';
          }, 1000);
        });
    }, true); // Use capture phase to intercept submit before theme listeners
  });
}

/**
 * Synchronized removal & quantity updates in Cart Drawer & Cart Page
 */
function initCartSyncHandlers() {
  document.addEventListener('click', function(e) {
    const removeBtn = e.target.closest('[data-remove-item]');
    const addBtn = e.target.closest('[data-add-quantity]');
    const subBtn = e.target.closest('[data-subtract-quantity]');

    if (!removeBtn && !addBtn && !subBtn) return;

    const itemEl = e.target.closest('[data-input-item]') || e.target.closest('[data-key]');
    if (!itemEl) return;

    const itemKey = itemEl.getAttribute('data-key');
    if (!itemKey) return;

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const targetItem = cart.items.find(i => i.key === itemKey);
        if (!targetItem) return;

        const bundleId = targetItem.properties?._bundle_id || targetItem.properties?._parent_id;
        if (!bundleId) return; // Not a bundled item

        const linkedItems = cart.items.filter(i => 
          i.properties?._bundle_id === bundleId || 
          i.properties?._parent_id === bundleId
        );

        if (linkedItems.length <= 1) return;

        // Synchronized Removal
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();

          const updates = {};
          linkedItems.forEach(i => {
            updates[i.key] = 0;
          });
          updateCartItems(updates);
          return;
        }

        // Synchronized Quantity Adjustments (+ / - buttons)
        if (addBtn || subBtn) {
          const qtyInput = itemEl.querySelector('[data-quantity-input]') || itemEl.querySelector('input[name="quantity"]');
          let currentQty = parseInt(qtyInput ? qtyInput.value : targetItem.quantity, 10);
          let newQty = addBtn ? currentQty + 1 : currentQty - 1;

          e.preventDefault();
          e.stopPropagation();

          const updates = {};
          linkedItems.forEach(i => {
            updates[i.key] = Math.max(0, newQty);
          });
          updateCartItems(updates);
        }
      });
  }, true);

  // Synchronized Direct Quantity Input changes
  document.addEventListener('change', function(e) {
    const qtyInput = e.target.closest('.quantity-input__input') || e.target.closest('[data-quantity-input]');
    if (!qtyInput) return;

    const itemEl = qtyInput.closest('[data-input-item]') || qtyInput.closest('[data-key]');
    if (!itemEl) return;

    const itemKey = itemEl.getAttribute('data-key');
    if (!itemKey) return;

    const newQty = parseInt(qtyInput.value, 10);

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const targetItem = cart.items.find(i => i.key === itemKey);
        if (!targetItem) return;

        const bundleId = targetItem.properties?._bundle_id || targetItem.properties?._parent_id;
        if (!bundleId) return;

        const linkedItems = cart.items.filter(i => 
          i.properties?._bundle_id === bundleId || 
          i.properties?._parent_id === bundleId
        );

        if (linkedItems.length <= 1) return;

        const updates = {};
        linkedItems.forEach(i => {
          updates[i.key] = Math.max(0, newQty);
        });

        updateCartItems(updates);
      });
  }, true);
}

function updateCartItems(updates) {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ updates: updates })
  })
  .then(r => r.json())
  .then(cart => {
    if (window.flu?.events) {
      window.flu.events.emit('cart:updated', { cart: cart });
      window.flu.events.emit('quick-cart:updated');
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('quick-cart:updated'));
  })
  .catch(err => console.error('Error updating cart items:', err));
}
